import InputField, { SIZE } from "@metacrm/metacrm-material-ui/dist/InputField";
import { Box } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import TestGround from "../aiTestGround/TestGround";
import CRMTable from "@metacrm/metacrm-material-ui/dist/Table";
import useGetTrainingData from "./useGetTrainingData";
import { map } from "lodash";
import TrainingEditModal from "./TrainingEditModal";
import { StyledThumbIcon } from "features/metadesk/ticket/Chat.styles";
import PopperMenu from "@metacrm/metacrm-material-ui/dist/PopperMenuNew";
import useNavigateEntity from "hooks/useNavigateEntity";
import ResponseThumb from "./ResponseThumb";
import { Loading } from "features/common";
import useGetPagePermission from "hooks/useGetPagePermission";
import { selectRemindBarShow } from "features/metadesk/redux/globalState/globalState.selector";

export default function TrainingArea() {
  const { formatMessage } = useIntl();

  const {
    data,
    headCells,
    page,
    setPage,
    dataCounts,
    fetchData,
    isLoading,
    filter,
    setFilter,
    canUpdateResponse,
    handleRefine,
    setCanUpdateResponse,
    search,
    setSearch,
  } = useGetTrainingData();

  const [edit, setEdit] = useState(null);

  const pagePermission = useGetPagePermission();
  const { readonly } = pagePermission("aiManagement");
  const remindBarShow = useSelector(selectRemindBarShow);

  useEffect(() => {
    fetchData();
  }, [filter, page]);

  const renderContents = (trainingData) => {
    const contents = [
      <Box fontSize={12} fontWeight={500} slot="input">
        {trainingData.userInput}
      </Box>,
      <Box fontSize={12} fontWeight={400} slot="response">
        <ResponseThumb data={trainingData} refresh={fetchData} />
      </Box>,
      <Box fontSize={12} fontWeight={400} slot="improvedResponse">
        <Box onClick={() => setEdit(trainingData)} sx={{ cursor: "pointer" }}>
          {trainingData?.improvedResponse || readonly ? (
            <Box color="#222">{trainingData?.improvedResponse}</Box>
          ) : (
            <Box
              component="i"
              className="meta-crm-icon-ic_edit-3 font-size-16"
              color="#A5A5A5"
            />
          )}
        </Box>
      </Box>,
    ];
    return contents;
  };

  return (
    <div>
      <Loading open={isLoading} />
      <div className="title">
        {formatMessage({ id: "global.module.trainingArea" })}
      </div>
      <div className="description">
        {formatMessage({ id: "global.description.trainingIntro" })}
      </div>
      <Box
        display="flex"
        alignItems="flex-end"
        justifyContent="space-between"
        mt="24px"
      >
        <Box className="item-flex" gap="0 12px">
          <InputField
            noBorder
            type="search"
            placeholder="Search"
            size={SIZE.MEDIUM}
            sx={{ width: "360px" }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onBlue={fetchData}
            onEnter={fetchData}
          />
          <PopperMenu
            noBorder
            type="filter"
            onMenuTitleButtonClick={() => setFilter({})}
            menuTitleButton={formatMessage({ id: "training.filter.clearAll" })}
            menuItemGroup={[
              {
                menuItemId: "AI Response Review",
                menuItemTitle: formatMessage({
                  id: "training.filter.aiResponseReview.title",
                }),
                menuItems: [
                  {
                    label: formatMessage({
                      id: "training.filter.aiResponseReview.thumbsUp",
                    }),
                    value: "thumbUp",
                    checked: filter.feedbackType === "thumbUp",
                  },
                  {
                    label: formatMessage({
                      id: "training.filter.aiResponseReview.thumbsDown",
                    }),
                    value: "thumbDown",
                    checked: filter.feedbackType === "thumbDown",
                  },
                ],
                onClick: (selected) => {
                  if (filter.feedbackType === selected.value)
                    return setFilter((prev) => ({
                      ...prev,
                      feedbackType: null,
                    }));
                  setFilter((prev) => ({
                    ...prev,
                    feedbackType: selected.value,
                  }));
                },
              },
              {
                menuItemId: "feedback",
                menuItemTitle: formatMessage({
                  id: "training.filter.feedbackProvider.title",
                }),
                menuItems: [
                  {
                    label: formatMessage({
                      id: "training.filter.feedbackProvider.endUser",
                    }),
                    value: "user",
                    checked: filter.feedbackFrom === "user",
                  },
                  {
                    label: formatMessage({
                      id: "training.filter.feedbackProvider.customerSupport",
                    }),
                    value: "admin",
                    checked: filter.feedbackFrom === "admin",
                  },
                ],
                onClick: (selected) => {
                  if (filter.feedbackFrom === selected.value)
                    return setFilter((prev) => ({
                      ...prev,
                      feedbackFrom: null,
                    }));
                  setFilter((prev) => ({
                    ...prev,
                    feedbackFrom: selected.value,
                  }));
                },
              },

              {
                menuItemId: "response",
                menuItemTitle: formatMessage({
                  id: "training.filter.responseImproved.title",
                }),
                menuItems: [
                  {
                    label: formatMessage({
                      id: "training.filter.responseImproved.notImproved",
                    }),
                    value: "false",
                    checked: filter.improvedResponse === "false",
                  },
                  {
                    label: formatMessage({
                      id: "training.filter.responseImproved.improved",
                    }),
                    value: "true",
                    checked: filter.improvedResponse === "true",
                  },
                ],
                onClick: (selected) => {
                  if (filter.improvedResponse === selected.value)
                    return setFilter((prev) => ({
                      ...prev,
                      improvedResponse: null,
                    }));
                  setFilter((prev) => ({
                    ...prev,
                    improvedResponse: selected.value,
                  }));
                },
              },
            ]}
          />
        </Box>
        <Box>
          {dataCounts} {formatMessage({ id: "global.commonWord.questions" })}
        </Box>
      </Box>
      <Box display="flex" gap={"0 12px"}>
        <Box flex={2}>
          <CRMTable
            containerStyle={{
              maxHeight: `calc(100vh - ${remindBarShow ? 206 : 180}px - ${
                dataCounts > 0 ? 30 : 0
              }px)`,
              marginTop: "8px",
            }}
            isLoading={isLoading}
            rowData={data}
            headers={headCells}
            noDataComponent={
              <Box
                className="item-flex"
                justifyContent={"center"}
                sx={{
                  flexDirection: "column",
                  minHeight: `calc(100vh - ${remindBarShow ? 232 : 268}px)`,
                }}
              >
                <img src={require("assets/img/empty_trainingArea.png")} />
                <Box mt="16px" color="#777">
                  {formatMessage({ id: "trainingArea.emptyStatus.noData" })}
                </Box>
              </Box>
            }
            contents={map(data, (user, i) => renderContents(user))}
            verticalAlign={"top"}
            page={page}
            setPage={setPage}
            dataCounts={dataCounts}
            empty
            rowsPerPage={20}
          />
        </Box>
        <Box flex={1} maxWidth={400} minWidth={400} mt="8px">
          <TestGround
            isTrainingArea={true}
            handleRefine={handleRefine}
            canUpdateResponse={canUpdateResponse}
            handleRefetchTrainingData={fetchData}
          />
        </Box>
      </Box>
      {edit && (
        <TrainingEditModal
          editInfo={edit}
          onClose={() => setEdit(null)}
          refresh={fetchData}
          setCanUpdateResponse={setCanUpdateResponse}
        />
      )}
    </div>
  );
}
